import React, { useState, useRef, useEffect } from "react";
import countries from "./countries.json";
import BringzzRadioList from "../BringzzRadioList";
import BringzzRadio from "../BringzzRadio";
import BringzzIcon from "../BringzzIcon";
import BringzzText from "../BringzzText";
import { twMerge } from "tailwind-merge";
import PropTypes from "prop-types";
import BringzzInput from "../BringzzInput";
import BringzzButton from "../BringzzButton";

const BringzzCountryDropdown = ({
  mode = "flag",
  onCountryChange,
  label,
  className,
  value, // Add value prop to receive the initial country
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    name: "Austria",
    dial_code: "+43",
    code: "AT",
  });
  const [confirm, setConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeLetter, setActiveLetter] = useState("a");
  const [startY, setStartY] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInitial, setIsInitial] = useState(true);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const countryRefs = useRef({});
  const listRef = useRef(null);

  useEffect(() => {
    if (value && value.name) {
      const country = countries.find(c => c.name.toLowerCase() === value.name.toLowerCase());
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value]);

  // Close modal with animation
  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setSearchTerm("");
      setIsClosing(false);
    }, 300); // Match transition duration
  };

  // Lock body scroll when modal is open (iOS fix) and reset states
  useEffect(() => {
    if (isModalOpen) {
      // Reset to initial state when modal opens
      setIsInitial(true);
      setIsFullscreen(false);
      setIsClosing(false);

      // Store original body styles
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;

      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        // Restore original styles
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isModalOpen]);

  const handleLetterClick = (letter) => {
    const countryKey = Object.keys(countryRefs.current).find((key) =>
      key.toLowerCase().startsWith(letter.toLowerCase())
    );

    if (countryKey && countryRefs.current[countryKey]) {
      const element = countryRefs.current[countryKey];
      if (listRef.current && element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  const alphabet = [
    "#",
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
    "u",
    "v",
    "w",
    "x",
    "y",
    "z",
  ];

  const modalRef = useRef(null);

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleDragStart = (event) => {
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    setStartY(clientY);
  };

  const handleDragEnd = (event) => {
    const endY = event.changedTouches
      ? event.changedTouches[0].clientY
      : event.clientY;
    const deltaY = startY - endY;

    if (deltaY < -50) {
      // Detect swipe down
      if (isFullscreen) {
        setIsFullscreen(false); // If fullscreen, return to initial state
        setIsInitial(true);
      } else if (isInitial) {
        closeModal(); // If initial and swiped down again, close the drawer with animation
      }
    } else if (deltaY > 50) {
      // Detect swipe up
      setIsFullscreen(true);
      setIsInitial(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      const handleClickOutsideModal = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          closeModal();
        }
      };
      document.addEventListener("mousedown", handleClickOutsideModal);
      return () => document.removeEventListener("mousedown", handleClickOutsideModal);
    }
  }, [isModalOpen]);

  const handleSelect = (country) => {
    setSelectedCountry(country);
    setConfirm(true);
    onCountryChange(country);
    setIsOpen(false);
  };

  const confirmSelection = () => {
    setIsOpen(false);
    if (mode === "modal") {
      closeModal();
    }
  };

  const getSortedCountries = (searchTerm) => {
    const trimmedSearch = searchTerm?.trim();
    if (!trimmedSearch) return countries;
    
    const filtered = countries.filter((country) =>
      country.name.toLowerCase().includes(trimmedSearch.toLowerCase())
    );
    
    return filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const search = trimmedSearch.toLowerCase();
      
      if (aName === search && bName !== search) return -1;
      if (bName === search && aName !== search) return 1;
      
      const aStarts = aName.startsWith(search);
      const bStarts = bName.startsWith(search);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      
      return aName.localeCompare(bName);
    });
  };

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  useEffect(() => {
    if (searchTerm) {
      const sorted = getSortedCountries(searchTerm);
      if (sorted.length > 0) {
        const firstLetter = sorted[0].name[0].toLowerCase();
        setActiveLetter(firstLetter);
      }
    } else {
      setActiveLetter("a");
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleScroll = () => {
      if (searchTerm) return;
      
      const visibleKeys = Object.keys(countryRefs.current).filter((key) => {
        const element = countryRefs.current[key];
        if (!element) return false;

        const rect = element.getBoundingClientRect();
        const scrollableRect = listRef.current.getBoundingClientRect();

        return rect.top >= scrollableRect.top && rect.top <= scrollableRect.bottom;
      });

      if (visibleKeys.length > 0) {
        const firstVisibleCountry = visibleKeys[0];
        const firstLetter = firstVisibleCountry[0].toLowerCase();

        if (firstLetter !== activeLetter.toLowerCase()) {
          setActiveLetter(firstLetter);
        }
      }
    };

    const listElement = listRef.current;
    if (listElement && !searchTerm) {
      listElement.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [listRef, isModalOpen, activeLetter, searchTerm]);

  return (
    <div className="relative h-full bg-inherit">
      {label && (
        <div className="flex items-center">
          <label htmlFor={"countryPicker"}>
            <BringzzText tag="h4" className="font-bold">
              {label}
            </BringzzText>
          </label>
        </div>
      )}
      <button
        className={twMerge(`w-full h-full py-1 rounded-md`, className)}
        onClick={() => (mode === "modal" ? toggleModal() : setIsOpen(!isOpen))}
      >
        {selectedCountry ? (
          <div className="flex items-center">
            <img
              src={`/countries/${selectedCountry.code}.svg`}
              alt={selectedCountry.code}
              className="w-5 h-5 rounded-full border border-white object-cover"
            />
            <div className="flex justify-between grow items-center">
              {mode !== "flag" && (
                <BringzzText tag="h5" className="">
                  {mode === "flag-name" && selectedCountry.name}
                </BringzzText>
              )}
              {mode === "modal" ? (
                <BringzzIcon
                  icon="IconChevronDown"
                  folder="LineIcons"
                  className="ml-1 h-4 w-4 text-slate-700"
                  size="16"
                />
              ) : (
                <BringzzIcon
                  icon="IconChevronDown"
                  folder="LineIcons"
                  className={twMerge(
                    "h-4 w-4 text-slate-600 transition-transform duration-200",
                    isOpen ? "rotate-180" : " "
                  )}
                  size="16"
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center">
            <div className="flex justify-between grow items-center">
              <BringzzText
                type={{
                  tag: "p",
                  classes: "text-md font-medium text-dark mx-4 text-center",
                }}
              >
                Select a country
              </BringzzText>
              {mode === "modal" ? (
                <BringzzIcon
                  icon="IconChevronDown"
                  folder="LineIcons"
                  className="mx-2 h-4 w-4 text-slate-700"
                  size="16"
                />
              ) : (
                <BringzzIcon
                  icon="IconChevronDown"
                  folder="LineIcons"
                  className={twMerge(
                    "h-4 w-4 text-slate-600 transition-transform duration-200",
                    isOpen ? "rotate-180" : " "
                  )}
                  size="16"
                />
              )}
            </div>
          </div>
        )}
      </button>

      {isOpen && mode !== "modal" && (
        <div className="absolute auto h-80 overflow-y-auto mt-1 pr-2 bg-white border border-gray-300 rounded-md shadow-lg z-10">
          {countries.map((country) => (
            <div
              key={country.code + "-select"}
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleSelect(country)}
            >
              <img
                src={`/countries/${country.code}.svg`}
                alt={country.code}
                className="w-6 h-4 mr-2"
              />
              {mode === "flag-name" && (
                <BringzzText
                  type={{
                    tag: "p",
                    classes: "text-md font-medium text-dark mx-4 text-center",
                  }}
                >
                  {country.name}
                </BringzzText>
              )}
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div
          className={`fixed inset-0 bg-black z-50 flex items-end justify-center transition-opacity duration-300 ${
            isClosing ? 'bg-opacity-0' : 'bg-opacity-50'
          }`}
          onClick={handleBackgroundClick}
          style={{
            height: '100vh',
            height: '100dvh',
            maxHeight: '-webkit-fill-available',
            touchAction: 'none'
          }}
        >
          <div
            className={`bg-white p-1 w-screen rounded-t-lg shadow-lg relative flex flex-col transition-all ease-in-out duration-300 overflow-hidden ${
              isFullscreen ? 'h-screen' : 'max-h-[80vh]'
            } ${isClosing ? 'translate-y-full' : 'translate-y-0'}`}
            ref={modalRef}
            style={{
              height: isFullscreen ? '100vh' : 'auto',
              maxHeight: isFullscreen ? '100vh' : '80vh'
            }}
          >
            <div className={`sticky top-0 left-0 right-0 bg-white z-50 shrink-0 shadow-sm ${isFullscreen ? 'pt-safe' : ''}`}
              style={isFullscreen ? { paddingTop: 'max(env(safe-area-inset-top), 20px)' } : {}}
            >
              <div
                className="flex justify-center py-2 cursor-pointer"
                onTouchStart={handleDragStart}
                onTouchEnd={handleDragEnd}
                onMouseDown={handleDragStart}
                onMouseUp={handleDragEnd}
              >
                <div className="bg-[#8B899B] h-2 w-16 rounded-lg"></div>
              </div>
              <div className="w-full flex justify-between items-center px-4 py-2 mb-2 h-12">
                <div className="flex items-center">
                  <div>&bull;</div>
                  <BringzzText className="font-bold text-xs">
                    {getSortedCountries(searchTerm).length === 0
                      ? "No result for your search"
                      : "Select a country "}
                  </BringzzText>
                </div>

                <BringzzIcon
                  size='16'
                  icon='IconClose'
                  folder='LineIcons'
                  className='cursor-pointer'
                  onClick={confirmSelection}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto flex items-start" ref={listRef}>
              <BringzzRadioList
                onChange={(newValue) => handleSelect(newValue)}
                className="w-full h-full pl-4 pr-1"
                state={{
                  checked: (
                    <BringzzIcon
                      icon="IconCheck"
                      library="Custom"
                      className="h-5 w-5 p-1 bg-magic-lilac text-light rounded-full"
                      size="12"
                    />
                  ),
                  unChecked: (
                    <div></div>
                  ),
                }}
              >
                {getSortedCountries(searchTerm)
                  .map((country) => (
                    <BringzzRadio
                      key={country.code + "-modal"}
                      value={country}
                      className="p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400"
                      state={{
                        checked: (
                          <BringzzIcon
                            icon="IconCheck"
                            library="Custom"
                            className="h-5 w-5 p-1 bg-magic-lilac text-light rounded-full"
                            size="12"
                          />
                        ),
                        unChecked: (
                          <div></div>
                        ),
                      }}
                      left={
                        <img
                          src={`/countries/${country.code}.svg`}
                          alt={country.code}
                          className="w-10 h-10 rounded-full"
                        />
                      }
                    >
                      <div
                        ref={(el) => (countryRefs.current[country.name] = el)}
                        className="flex"
                      >
                        <BringzzText tag="h3" className="font-bold w-1/5">
                          {country.dial_code}
                        </BringzzText>
                        <BringzzText tag="h3" className="font-bold">
                          {country.name}
                        </BringzzText>
                      </div>
                    </BringzzRadio>
                  ))}
              </BringzzRadioList>
              <div className="flex flex-col">
                {alphabet &&
                  alphabet.map((letter, index) => {
                    const isActive = letter.toLowerCase() === activeLetter.toLowerCase();
                    return (
                      <div
                        key={index}
                        onClick={() => handleLetterClick(letter)}
                        className={twMerge("cursor-pointer text-center", isActive && "bg-magic-lilac")}
                      >
                        <BringzzText
                          tag="h5"
                          className="text-midnight-blue/60 font-normal"
                        >
                          {letter.toUpperCase()}
                        </BringzzText>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className={`sticky bottom-0 left-0 right-0 p-4 shrink-0 border-t space-y-4 bg-white z-50 ${isFullscreen ? 'pb-safe' : ''}`}
              style={isFullscreen ? { paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' } : {}}
            >
              <BringzzInput
                id="countryPicker"
                type="text"
                placeholder="e.g. Germany"
                icon={{
                  left: (
                    <BringzzIcon
                      icon="IconSearchAlt"
                      folder="LineIcons"
                      size="20"
                      className="text-slate-400"
                    />
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className=" p-3 border rounded-full "
                inputProps={{
                  onFocus: (e) => {
                    setIsSearchFocused(true);
                    // Prevent iOS from scrolling the page when keyboard appears
                    e.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                  },
                  onBlur: () => setIsSearchFocused(false),
                }}
              />
              {!isSearchFocused && (
                <BringzzButton
                  className="w-full flex justify-center bg-magic-lilac"
                  disabled={!confirm}
                  onClick={() => confirmSelection()}
                >
                  {!confirm ? "Select a country" : "Confirm"}
                </BringzzButton>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BringzzCountryDropdown.propTypes = {
  mode: PropTypes.oneOf(["flag", "flag-name", "modal"]),
  onCountryChange: PropTypes.func.isRequired,
  value: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.string,
  }),
};

export default BringzzCountryDropdown;
